/**
 * Messages repository.
 *
 * Every query that touches the `messages` table lives here. See
 * `src/data/README.md` for the rules of the repository layer.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { unwrap } from '@/lib/supabase-helpers';
import type { Database, Tables, TablesInsert } from '@/integrations/supabase/types';

export type Message = Tables<'messages'>;
export type MessageInsert = TablesInsert<'messages'>;

/**
 * Supabase-Client-Shape, den mutierende Repo-Funktionen akzeptieren
 * duerfen. Default ist der Therapeuten-Client (`supabase`); Portal-
 * Aufrufer koennen `supabasePortal` uebergeben, damit RLS und Session-
 * Storage unter dem Portal-Auth-Kontext laufen.
 */
export type DbClient = SupabaseClient<Database>;

/** Roles that count as "incoming" messages requiring a therapist response. */
export const INBOUND_ROLES = ['patient', 'external_email'] as const;

/**
 * Count unread messages from patients and inbound emails.
 *
 * Used by the sidebar badge — should stay cheap. Uses a HEAD request so
 * Supabase only returns the count, not the rows.
 */
export async function countUnreadInbound(orgId: string): Promise<number> {
  const { count, error } = await supabase
    .from('messages')
    .select('*', { count: 'exact', head: true })
    .eq('organization_id', orgId)
    .in('sender_role', [...INBOUND_ROLES])
    .is('read_at', null);
  if (error) throw error;
  return count ?? 0;
}

/** List messages for an organisation, newest first. */
export async function listMessages(orgId: string, limit = 50): Promise<Message[]> {
  return unwrap(
    supabase
      .from('messages')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })
      .limit(limit),
  );
}

/**
 * The shape returned by the dashboard "unread messages" widget — includes
 * a denormalised patient name so the widget can render without a separate
 * fetch. Cast at the boundary because the join table is not yet declared
 * in the generated Supabase types.
 */
export interface UnreadMessageWithPatient {
  id: string;
  patient_id: string | null;
  content: string;
  sender_role: string;
  read_at: string | null;
  created_at: string | null;
  patients: { first_name: string; last_name: string } | null;
}

/**
 * List recent unread inbound messages with denormalised patient names.
 * Used by the dashboard widget — limited to 8 by default to keep the
 * payload tiny.
 */
export async function listUnreadInbound(
  orgId: string,
  limit = 8,
): Promise<UnreadMessageWithPatient[]> {
  const { data, error } = await supabase
    .from('messages')
    .select('id, patient_id, content, sender_role, read_at, created_at, patients(first_name, last_name)')
    .eq('organization_id', orgId)
    .is('read_at', null)
    .in('sender_role', [...INBOUND_ROLES])
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as unknown as UnreadMessageWithPatient[];
}

/**
 * Send a new message.
 *
 * Optional `client`-Parameter erlaubt Portal-Aufrufen (supabasePortal) das
 * Insert unter der Patient-Session auszufuehren. Ohne Argument wird der
 * Standard-Client verwendet (Therapeuten-Kontext).
 */
export async function sendMessage(
  input: MessageInsert,
  client: DbClient = supabase,
): Promise<Message> {
  return unwrap(
    client.from('messages').insert([input]).select('*').single(),
  );
}

/**
 * Mark every inbound message for a patient as read. Run when a therapist
 * opens the conversation so the unread badge updates immediately.
 */
export async function markPatientThreadRead(
  orgId: string,
  patientId: string,
): Promise<void> {
  const { error } = await supabase
    .from('messages')
    .update({ read_at: new Date().toISOString() })
    .eq('organization_id', orgId)
    .eq('patient_id', patientId)
    .in('sender_role', [...INBOUND_ROLES])
    .is('read_at', null);
  if (error) throw error;
}
